import React from "react";
import {
  Box,
  Paper,
  Typography,
  Icon,
  Button,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  alpha,
  useTheme,
  FormControl,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { LoadingState } from "./LoadingState";

interface StaffBuilderFacilityAssignmentProps {
  selectedAssignment?: any;
  assignmentOptions: any[];
  errors: {
    assignment?: string;
  };
  dataLoading: boolean;
  onAssignmentSelect: (assignment: any) => void;
}

const CustomTooltip = (props: any) => (
  <Tooltip
    {...props}
    arrow
    PopperProps={{
      sx: {
        "& .MuiTooltip-tooltip": {
          backgroundColor: "background.paper",
          color: "text.primary",
          boxShadow: 4,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          padding: "8px 12px",
          fontSize: 13,
          fontWeight: 500,
          maxWidth: "none",
          display: "flex",
          alignItems: "center",
          gap: 1,
        },
        "& .MuiTooltip-arrow": {
          color: "background.paper",
          "&:before": {
            border: "1px solid",
            borderColor: "divider",
          },
        },
      },
    }}
  />
);

// Update navigation paths
const ROUTES = {
  BRANCHES: "/business/configuration/branches",
  WAREHOUSES: "/business/configuration/warehouses",
  DEPARTMENTS: "/business/configuration/departments",
};

export const StaffBuilderFacilityAssignment: React.FC<
  StaffBuilderFacilityAssignmentProps
> = ({
  selectedAssignment,
  assignmentOptions,
  errors,
  dataLoading,
  onAssignmentSelect,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [branchesMenuAnchor, setBranchesMenuAnchor] =
    React.useState<null | HTMLElement>(null);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: "1px solid",
        borderColor: alpha("#0891B2", 0.1),
        borderRadius: "12px",
        bgcolor: "background.paper",
        transition: "all 0.3s ease-in-out",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        "&:hover": {
          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            color: "#0891B2",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontFamily: "Montserrat",
            fontWeight: 600,
            fontSize: "0.95rem",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#0891B2", 0.1),
            }}
          >
            <Icon>business</Icon>
          </Box>
          Facility Assignment
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontFamily: "Montserrat",
            mt: 1,
            fontSize: "0.875rem",
          }}
        >
          Assign the staff member to a branch, warehouse, or department
        </Typography>
      </Box>

      <FormControl fullWidth error={!!errors.assignment}>
        <Box>
          {/* Selected Assignment Display */}
          {selectedAssignment ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
                p: 2,
                bgcolor: alpha("#0891B2", 0.04),
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "#0891B2",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha("#0891B2", 0.1),
                }}
              >
                <Icon sx={{ color: "#0891B2" }}>{selectedAssignment.icon}</Icon>
              </Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontFamily: "Montserrat",
                  fontSize: "0.875rem",
                }}
              >
                {selectedAssignment.name}
              </Typography>
              <Chip
                size="small"
                label={
                  selectedAssignment.type === "department"
                    ? "Department"
                    : selectedAssignment.type === "warehouse"
                    ? "Warehouse"
                    : "Branch"
                }
                sx={{
                  ml: "auto",
                  bgcolor: alpha("#0891B2", 0.1),
                  color: "#0891B2",
                  fontFamily: "Montserrat",
                  fontSize: "0.75rem",
                  height: 24,
                  borderRadius: "6px",
                }}
              />
              <IconButton
                size="small"
                onClick={() => onAssignmentSelect(undefined)}
                sx={{
                  ml: 1,
                  color: "text.secondary",
                  "&:hover": {
                    bgcolor: alpha("#0891B2", 0.08),
                  },
                }}
              >
                <Icon fontSize="small">close</Icon>
              </IconButton>
            </Box>
          ) : (
            <Typography
              color="text.secondary"
              sx={{
                mb: 2,
                fontFamily: "Montserrat",
                fontSize: "0.875rem",
              }}
            >
              Select a branch, warehouse, or department where the employee is
              working
            </Typography>
          )}

          <Grid container spacing={2}>
            {/* Branches & Warehouses Section */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  borderColor: alpha("#0891B2", 0.1),
                  overflow: "hidden",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "#0891B2",
                  },
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: alpha("#0891B2", 0.02),
                    borderBottom: "1px solid",
                    borderColor: alpha("#0891B2", 0.1),
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha("#0891B2", 0.1),
                    }}
                  >
                    <Icon sx={{ color: "#0891B2", fontSize: "1.25rem" }}>
                      business
                    </Icon>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontFamily: "Montserrat",
                      fontSize: "0.875rem",
                    }}
                  >
                    Branches & Warehouses
                  </Typography>
                  <CustomTooltip
                    title={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Icon fontSize="small" sx={{ color: "#0891B2" }}>
                          launch
                        </Icon>
                        <span>Manage Branches & Warehouses</span>
                      </Box>
                    }
                    placement="top"
                  >
                    <Button
                      size="small"
                      startIcon={<Icon fontSize="small">launch</Icon>}
                      onClick={(e) => setBranchesMenuAnchor(e.currentTarget)}
                      sx={{
                        bgcolor: alpha("#0891B2", 0.08),
                        color: "#0891B2",
                        "&:hover": {
                          bgcolor: alpha("#0891B2", 0.12),
                        },
                        minWidth: "auto",
                        px: 1.5,
                        ml: "auto",
                        fontFamily: "Montserrat",
                        fontSize: "0.75rem",
                        borderRadius: "8px",
                        textTransform: "none",
                      }}
                    >
                      Manage
                    </Button>
                  </CustomTooltip>
                </Box>
                <Box sx={{ maxHeight: 300, overflow: "auto", p: 1.5 }}>
                  {dataLoading ? (
                    <LoadingState
                      variant="list"
                      count={3}
                      withIcon
                      withChip
                      spacing={1}
                    />
                  ) : assignmentOptions.filter(
                      (opt) => opt.type === "branch" || opt.type === "warehouse"
                    ).length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha("#0891B2", 0.1),
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <Icon sx={{ color: "#0891B2", fontSize: "2rem" }}>
                          business
                        </Icon>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "Montserrat",
                          fontSize: "0.875rem",
                          color: "text.secondary",
                          mb: 1,
                        }}
                      >
                        No branches or warehouses found
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<Icon>add</Icon>}
                        onClick={(e) => setBranchesMenuAnchor(e.currentTarget)}
                        sx={{
                          bgcolor: alpha("#0891B2", 0.08),
                          color: "#0891B2",
                          "&:hover": {
                            bgcolor: alpha("#0891B2", 0.12),
                          },
                          fontFamily: "Montserrat",
                          fontSize: "0.75rem",
                          borderRadius: "8px",
                          textTransform: "none",
                        }}
                      >
                        Add Facility
                      </Button>
                    </Box>
                  ) : (
                    assignmentOptions
                      .filter(
                        (opt) =>
                          opt.type === "branch" || opt.type === "warehouse"
                      )
                      .map((option) => (
                        <Button
                          key={option._id}
                          fullWidth
                          onClick={() => onAssignmentSelect(option)}
                          sx={{
                            justifyContent: "flex-start",
                            p: 1.5,
                            mb: 0.5,
                            borderRadius: "8px",
                            textAlign: "left",
                            bgcolor:
                              selectedAssignment?._id === option._id
                                ? alpha("#0891B2", 0.08)
                                : "transparent",
                            "&:hover": {
                              bgcolor: alpha("#0891B2", 0.08),
                            },
                            fontFamily: "Montserrat",
                            textTransform: "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha("#0891B2", 0.1),
                                transition: "all 0.2s ease-in-out",
                                transform:
                                  selectedAssignment?._id === option._id
                                    ? "scale(1.1)"
                                    : "scale(1)",
                              }}
                            >
                              <Icon
                                sx={{
                                  fontSize: "1.25rem",
                                  color:
                                    selectedAssignment?._id === option._id
                                      ? "#0891B2"
                                      : "action.active",
                                }}
                              >
                                {option.icon}
                              </Icon>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "Montserrat",
                                fontSize: "0.875rem",
                                color:
                                  selectedAssignment?._id === option._id
                                    ? "#0891B2"
                                    : "text.primary",
                                fontWeight:
                                  selectedAssignment?._id === option._id
                                    ? 500
                                    : 400,
                              }}
                            >
                              {option.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                option.type === "warehouse"
                                  ? "Warehouse"
                                  : "Branch"
                              }
                              sx={{
                                ml: "auto",
                                bgcolor:
                                  selectedAssignment?._id === option._id
                                    ? alpha("#0891B2", 0.1)
                                    : alpha(theme.palette.action.active, 0.08),
                                color:
                                  selectedAssignment?._id === option._id
                                    ? "#0891B2"
                                    : "text.secondary",
                                fontFamily: "Montserrat",
                                fontSize: "0.75rem",
                                height: 24,
                                borderRadius: "6px",
                              }}
                            />
                          </Box>
                        </Button>
                      ))
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Departments Section - Similar updates as above */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  borderColor: alpha("#0891B2", 0.1),
                  overflow: "hidden",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "#0891B2",
                  },
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: alpha("#0891B2", 0.02),
                    borderBottom: "1px solid",
                    borderColor: alpha("#0891B2", 0.1),
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha("#0891B2", 0.1),
                    }}
                  >
                    <Icon sx={{ color: "#0891B2", fontSize: "1.25rem" }}>
                      domain
                    </Icon>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontFamily: "Montserrat",
                      fontSize: "0.875rem",
                    }}
                  >
                    Departments
                  </Typography>
                  <CustomTooltip
                    title={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Icon fontSize="small" sx={{ color: "#0891B2" }}>
                          launch
                        </Icon>
                        <span>Manage Departments</span>
                      </Box>
                    }
                    placement="top"
                  >
                    <Button
                      size="small"
                      startIcon={<Icon fontSize="small">launch</Icon>}
                      onClick={() => navigate(ROUTES.DEPARTMENTS)}
                      sx={{
                        bgcolor: alpha("#0891B2", 0.08),
                        color: "#0891B2",
                        "&:hover": {
                          bgcolor: alpha("#0891B2", 0.12),
                        },
                        minWidth: "auto",
                        px: 1.5,
                        ml: "auto",
                        fontFamily: "Montserrat",
                        fontSize: "0.75rem",
                        borderRadius: "8px",
                        textTransform: "none",
                      }}
                    >
                      Manage
                    </Button>
                  </CustomTooltip>
                </Box>
                <Box sx={{ maxHeight: 300, overflow: "auto", p: 1.5 }}>
                  {dataLoading ? (
                    <LoadingState
                      variant="list"
                      count={3}
                      withIcon
                      withChip
                      spacing={1}
                    />
                  ) : assignmentOptions.filter(
                      (opt) => opt.type === "department"
                    ).length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha("#0891B2", 0.1),
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <Icon sx={{ color: "#0891B2", fontSize: "2rem" }}>
                          domain
                        </Icon>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "Montserrat",
                          fontSize: "0.875rem",
                          color: "text.secondary",
                          mb: 1,
                        }}
                      >
                        No departments found
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<Icon>add</Icon>}
                        onClick={() => navigate(ROUTES.DEPARTMENTS)}
                        sx={{
                          bgcolor: alpha("#0891B2", 0.08),
                          color: "#0891B2",
                          "&:hover": {
                            bgcolor: alpha("#0891B2", 0.12),
                          },
                          fontFamily: "Montserrat",
                          fontSize: "0.75rem",
                          borderRadius: "8px",
                          textTransform: "none",
                        }}
                      >
                        Add Department
                      </Button>
                    </Box>
                  ) : (
                    assignmentOptions
                      .filter((opt) => opt.type === "department")
                      .map((option) => (
                        <Button
                          key={option._id}
                          fullWidth
                          onClick={() => onAssignmentSelect(option)}
                          sx={{
                            justifyContent: "flex-start",
                            p: 1.5,
                            mb: 0.5,
                            borderRadius: "8px",
                            textAlign: "left",
                            bgcolor:
                              selectedAssignment?._id === option._id
                                ? alpha("#0891B2", 0.08)
                                : "transparent",
                            "&:hover": {
                              bgcolor: alpha("#0891B2", 0.08),
                            },
                            fontFamily: "Montserrat",
                            textTransform: "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha("#0891B2", 0.1),
                                transition: "all 0.2s ease-in-out",
                                transform:
                                  selectedAssignment?._id === option._id
                                    ? "scale(1.1)"
                                    : "scale(1)",
                              }}
                            >
                              <Icon
                                sx={{
                                  fontSize: "1.25rem",
                                  color:
                                    selectedAssignment?._id === option._id
                                      ? "#0891B2"
                                      : "action.active",
                                }}
                              >
                                {option.icon}
                              </Icon>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "Montserrat",
                                fontSize: "0.875rem",
                                color:
                                  selectedAssignment?._id === option._id
                                    ? "#0891B2"
                                    : "text.primary",
                                fontWeight:
                                  selectedAssignment?._id === option._id
                                    ? 500
                                    : 400,
                              }}
                            >
                              {option.name}
                            </Typography>
                            <Chip
                              size="small"
                              label="Department"
                              sx={{
                                ml: "auto",
                                bgcolor:
                                  selectedAssignment?._id === option._id
                                    ? alpha("#0891B2", 0.1)
                                    : alpha(theme.palette.action.active, 0.08),
                                color:
                                  selectedAssignment?._id === option._id
                                    ? "#0891B2"
                                    : "text.secondary",
                                fontFamily: "Montserrat",
                                fontSize: "0.75rem",
                                height: 24,
                                borderRadius: "6px",
                              }}
                            />
                          </Box>
                        </Button>
                      ))
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        {errors.assignment && (
          <FormHelperText
            error
            sx={{
              fontFamily: "Montserrat",
              mt: 1,
              fontSize: "0.75rem",
            }}
          >
            {errors.assignment}
          </FormHelperText>
        )}
      </FormControl>

      {/* Branches Menu */}
      <Menu
        anchorEl={branchesMenuAnchor}
        open={Boolean(branchesMenuAnchor)}
        onClose={() => setBranchesMenuAnchor(null)}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: `0 4px 20px 0 ${alpha("#000000", 0.08)}`,
            border: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
            borderRadius: "8px",
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate(ROUTES.BRANCHES);
            setBranchesMenuAnchor(null);
          }}
          sx={{
            py: 1,
            px: 2,
            borderRadius: "6px",
            m: 0.5,
            gap: 1.5,
            fontFamily: "Montserrat",
            fontSize: "0.875rem",
            color: "text.primary",
            "&:hover": {
              bgcolor: alpha("#0891B2", 0.08),
            },
          }}
        >
          <Icon fontSize="small" sx={{ color: "#0891B2" }}>
            list
          </Icon>
          Manage Branches
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(ROUTES.WAREHOUSES);
            setBranchesMenuAnchor(null);
          }}
          sx={{
            py: 1,
            px: 2,
            borderRadius: "6px",
            m: 0.5,
            gap: 1.5,
            fontFamily: "Montserrat",
            fontSize: "0.875rem",
            color: "text.primary",
            "&:hover": {
              bgcolor: alpha("#0891B2", 0.08),
            },
          }}
        >
          <Icon fontSize="small" sx={{ color: "#0891B2" }}>
            list
          </Icon>
          Manage Warehouses
        </MenuItem>
      </Menu>
    </Paper>
  );
};
